import { type ErrorLogService as ErrorLogServiceClass } from 'ErrorLogging';
import { angularInjectorProvider } from 'Injector';
import { type TranslationHelperClass } from 'Translation';
import { getCohort, type CurrentUser } from 'Users';
import { getSupportEmailAddress } from 'Cohorts';
import { type ConfigFactory as ConfigFactoryType } from 'FrontRoyalConfig';
import { type StreamForDashboard, getExamOpenTime } from 'Lessons';
import { targetBrandConfig } from 'AppBranding';

type GenericContentItem = { id: string };
function isStream(contentItem: StreamForDashboard | GenericContentItem): contentItem is StreamForDashboard {
    return 'chapters' in contentItem;
}

export function getReasonText({
    usage,
    contentType,
    contentItem,
    reason,
    user,
}: {
    usage: 'message' | 'title';
    reason: string;
    contentType: 'lesson' | 'stream' | 'playlist';
    contentItem: StreamForDashboard | GenericContentItem;
    user: CurrentUser | null;
}) {
    // Text will use a key like
    // reason_message_lesson_locked or reason_title_stream_unmet_prerequisites
    let key;
    const defaultKey = ['reason', usage, contentType, 'default'].join('_');
    if (!reason) {
        notifyOnMissingLaunchReason({ reason, contentType, contentItemId: contentItem.id });
        key = defaultKey;
    } else {
        key = ['reason', usage, contentType, reason].join('_');
    }

    let examOpenTime: Date | null = null;
    if (isStream(contentItem)) {
        examOpenTime = getExamOpenTime({ user, stream: contentItem });
    }

    const ConfigFactory = angularInjectorProvider.get<ConfigFactoryType>('ConfigFactory');
    const brandEmail = targetBrandConfig(user, ConfigFactory.getSync()).emailAddressForUsername('support');
    const cohort = getCohort(user);
    const registrarEmail = cohort ? getSupportEmailAddress(cohort, 'registrar') : null;
    const openTimeStr = examOpenTime?.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });

    const TranslationHelper = angularInjectorProvider.get<TranslationHelperClass>('TranslationHelper');
    const translationHelper = new TranslationHelper('lessons.shared.content_access_helper');

    // try to translate the key.  If it is not found, translationHelper
    // will notify the ErrorLogService and fall back to the default locked message
    return translationHelper.getWithFallbackKey(key, defaultKey, {
        // any params that will be needed for any of these translations
        // should be included here
        openTimeStr,
        brandEmail,
        // The registrarEmail is used in the placed_on_academic_hold. Presumably, whenever we're using that,
        // there will be a relevantCohort and a registrar email. Just in case, though, add a fallback
        // to the brandEmail
        registrarEmail: registrarEmail || brandEmail,
    });
}

const notifiedReasons = new Set<string>();

function notifyOnMissingLaunchReason({
    reason,
    contentType,
    contentItemId,
}: {
    reason: string;
    contentType: string;
    contentItemId: string;
}) {
    if (notifiedReasons.has(reason)) {
        return;
    }

    const ErrorLogService = angularInjectorProvider.safeGet<typeof ErrorLogServiceClass>('ErrorLogService');
    if (!ErrorLogService) return;
    notifiedReasons.add(reason);

    ErrorLogService.notify('reason not set.', {
        contentType,
        contentItemId,
    });
}
